import { useQuery } from "@tanstack/react-query";
import { Flame, Minus, Plus, RefreshCw } from "lucide-react";

import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { ConversaLabels, HojeLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";
import type { LoadedIntelligence } from "@/types/cockpit";

import { ConversationContextFacts } from "./facts";

const temperature = {
	hot: { label: "Quente", tone: "bg-destructive-soft text-destructive" },
	warm: { label: "Morno", tone: "bg-warning-soft text-warning" },
	cold: { label: "Frio", tone: "bg-muted text-muted-foreground" },
};

export function ConversationContextIntelligence({ personId }: { personId: number }) {
	const { data, isPending, isError, refetch } = useQuery(
		orpc.cockpit.intelligence.queryOptions({ input: { person_id: personId } }),
	);

	return (
		<section className="flex-1 px-6 py-5" aria-label="Resumo da conversa">
			{isPending && (
				<div className="animate-pulse space-y-6" aria-label="Carregando resumo">
					<div className="flex items-center justify-between border-b border-border pb-5">
						<div className="h-6 w-24 rounded bg-border/60" />
						<div className="h-6 w-16 rounded bg-border/60" />
					</div>
					<div className="h-4 w-2/3 rounded bg-border/60" />
					<div className="space-y-4 border-l border-border pl-5">
						<div className="h-20 rounded bg-border/40" />
						<div className="h-20 rounded bg-border/40" />
					</div>
				</div>
			)}
			{isError && (
				<div className="space-y-2 text-xs text-muted-foreground" role="alert">
					<p>Não foi possível carregar o resumo.</p>
					<Button variant="ghost" size="sm" onClick={() => refetch()}>
						<RefreshCw /> Tentar de novo
					</Button>
				</div>
			)}
			{!isPending && !isError && !data && (
				<p className="text-sm leading-relaxed text-muted-foreground">
					Esta conversa ainda não tem resumo.
				</p>
			)}
			{data && <ConversationContextIntelligenceContent intelligence={data} />}
		</section>
	);
}

function ConversationContextIntelligenceContent({
	intelligence,
}: {
	intelligence: LoadedIntelligence;
}) {
	return (
		<>
			<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-5">
				{intelligence.state.funnel_stage !== "not_applicable" && (
					<h3 className="font-display text-2xl text-primary">
						{HojeLabels.funnelStage[intelligence.state.funnel_stage]}
					</h3>
				)}
				<span
					className={cn(
						"inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
						temperature[intelligence.state.temperatura].tone,
					)}
				>
					{intelligence.state.temperatura === "hot" && <Flame className="size-3" />}
					{temperature[intelligence.state.temperatura].label}
				</span>
			</div>
			{!!intelligence.state.summary && (
				<details className="group mt-4">
					<summary className="flex min-h-8 cursor-pointer list-none items-center justify-between gap-3 rounded-sm text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
						Resumo da conversa
						<Plus className="size-4 group-open:hidden" />
						<Minus className="hidden size-4 group-open:block" />
					</summary>
					<p className="mt-3 break-words text-sm leading-6 text-muted-foreground">
						{intelligence.state.summary}
					</p>
				</details>
			)}
			{intelligence.facts.length > 0 && <ConversationContextFacts facts={intelligence.facts} />}
			{(!!intelligence.state.profile || !!intelligence.state.communication) && (
				<dl className="mt-6 space-y-5 border-t border-border pt-5">
					{!!intelligence.state.profile && (
						<div>
							<dt className="text-xs font-semibold text-primary">{ConversaLabels.work.profile}</dt>
							<dd className="mt-2 break-words text-sm leading-6 text-muted-foreground">
								{intelligence.state.profile}
							</dd>
						</div>
					)}
					{!!intelligence.state.communication && (
						<div>
							<dt className="text-xs font-semibold text-primary">
								{ConversaLabels.work.communication}
							</dt>
							<dd className="mt-2 break-words text-sm leading-6 text-muted-foreground">
								{intelligence.state.communication}
							</dd>
						</div>
					)}
				</dl>
			)}
		</>
	);
}
